import { Button, Chip, Icons } from "@jod/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  CUSTOMER_DEMO_TABLE_ID,
  CUSTOMER_DEMO_VENUE_ID,
  customerBackendApi,
} from "../features/api/backend.ts";
import { useT } from "../features/i18n/index.ts";
import { useLiff } from "../features/liff/use-liff.ts";
import { useSession } from "../features/session/session-context.tsx";
import { findJoinTableByToken, mockJoinTables, mockVenue } from "../mocks/data.ts";

export const Route = createFileRoute("/join-table")({
  validateSearch: (search: Record<string, unknown>): { t?: string; branch?: string } => ({
    t: typeof search.t === "string" ? search.t : undefined,
    branch: typeof search.branch === "string" ? search.branch : undefined,
  }),
  component: JoinTableScreen,
});

function JoinTableScreen() {
  const t = useT();
  const navigate = useNavigate();
  const { t: qrToken } = Route.useSearch();
  const { joinTable } = useSession();
  const { profile } = useLiff();

  // Confirm-first: the customer arrives by scanning a table QR, so the table is
  // already known from the entry context (FR-CUS-002 / ADR-0008). We resolve it
  // from the QR token and just ask them to confirm — never make them pick.
  // With no token (mock/dev) we simulate the scanned table so the default path
  // still shows the confirm screen. The picker is a fallback, revealed only when
  // the table genuinely isn't known (invalid token, or "not my table").
  const tokenTable = useMemo(() => findJoinTableByToken(qrToken), [qrToken]);
  const scannedTable = tokenTable ?? (qrToken ? undefined : mockJoinTables[0]);
  const tokenMissing = Boolean(qrToken && !tokenTable);

  const [showPicker, setShowPicker] = useState(tokenMissing);
  const [code, setCode] = useState(scannedTable?.code ?? mockJoinTables[0]?.code ?? "B4");
  const table = mockJoinTables.find((candidate) => candidate.code === code) ?? mockJoinTables[0];
  const isOrderOnly = table?.status === "order_only";

  const backendEnabled = customerBackendApi !== null;
  // Resolve the real backend table id for the chosen code so the check-in
  // targets the seeded session table (falls back to the demo table id).
  const tablesQuery = useQuery({
    queryKey: ["customer", "tables", CUSTOMER_DEMO_VENUE_ID],
    queryFn: () =>
      customerBackendApi?.listTables(CUSTOMER_DEMO_VENUE_ID) ??
      Promise.reject(new Error("Customer backend API unavailable")),
    enabled: backendEnabled,
  });

  const enter = () => {
    if (!table) return;
    const tableId = backendEnabled
      ? (tablesQuery.data?.find((row) => row.code === table.code)?.id ?? CUSTOMER_DEMO_TABLE_ID)
      : undefined;
    joinTable(table.code, { tableId, displayName: profile?.displayName });
    navigate({ to: table.status === "order_only" ? "/home" : "/tonight-mode" });
  };

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-md flex-col justify-center gap-6 px-7 py-10">
      <div className="text-center">
        <div className="jod-kicker">{t("joinTable.kicker")}</div>
        <h1 className="h-display mt-1 text-[1.6rem]">
          {isOrderOnly ? t("joinTable.orderOnlyTitle") : t("joinTable.confirmTitle")}
        </h1>
        <p className="mt-1 text-cream-3 text-sm">{mockVenue.name}</p>
      </div>

      {tokenMissing ? (
        <div className="glass rounded-xl p-4 text-cream-2 text-sm">
          {t("joinTable.tokenInvalid")}
        </div>
      ) : null}

      <div className="glass-hi relative overflow-hidden rounded-xl px-5 py-6">
        <div className="jod-sheen" aria-hidden="true" />
        <div className="relative flex items-center justify-between">
          <div>
            <div className="jod-kicker">
              {isOrderOnly ? t("joinTable.branch") : t("joinTable.tableKicker")}
            </div>
            <div className="h-display text-[2.4rem] text-gold leading-none">{table?.code}</div>
            <div className="mt-1 text-cream-3 text-sm">{table?.zone}</div>
          </div>
          <span className="grid size-16 place-items-center rounded-lg border border-hairline bg-[oklch(100%_0_0_/_4%)] text-gold">
            <Icons.IconQR size={36} />
          </span>
        </div>

        <div className="relative my-5">
          <span className="jod-notch" style={{ left: -31 }} aria-hidden="true" />
          <span className="jod-notch" style={{ right: -31 }} aria-hidden="true" />
          <div className="border-[var(--hairline-strong)] border-t border-dashed" />
        </div>

        <div className="relative space-y-2 text-cream-2 text-sm">
          <div className="flex items-center gap-2">
            <Icons.IconPeople size={16} />{" "}
            {t("joinTable.friendsCheckedIn", { count: table?.friendsCheckedIn ?? 0 })}
          </div>
          <div className="flex items-start gap-2 text-cream-3 text-xs leading-relaxed">
            <Icons.IconShield size={14} className="mt-0.5" /> {table?.helperTh}
          </div>
        </div>
      </div>

      {showPicker ? (
        // Fallback: the table isn't known (invalid QR, branch-level QR, or the
        // customer said "not my table") — let them pick or fall back to walk-in.
        <div className="space-y-3">
          <p className="text-center text-cream-3 text-xs">{t("joinTable.pickHint")}</p>
          <div className="flex flex-wrap justify-center gap-2">
            {mockJoinTables.map((candidate) => (
              <Chip
                key={candidate.code}
                on={candidate.code === code}
                onClick={() => setCode(candidate.code)}
              >
                {candidate.status === "order_only"
                  ? t("joinTable.noTableChip")
                  : t("common.table", { code: candidate.code })}
              </Chip>
            ))}
          </div>
          <Button size="lg" className="w-full" onClick={enter}>
            {isOrderOnly ? t("joinTable.enterOrderOnly") : t("joinTable.enterTable")}
          </Button>
        </div>
      ) : (
        // Default confirm-first path: one tap to join the scanned table.
        <div className="space-y-3">
          <Button size="lg" className="w-full" onClick={enter}>
            {isOrderOnly ? t("joinTable.enterOrderOnly") : t("joinTable.enterConfirm")}
          </Button>
          <button
            type="button"
            onClick={() => setShowPicker(true)}
            className="mx-auto block text-cream-3 text-xs underline-offset-2 hover:underline"
          >
            {t("joinTable.notMyTable")}
          </button>
        </div>
      )}
    </div>
  );
}
